import { useState } from 'react';
import { Input } from '../../../shared/components/Input';
import { Select } from '../../../shared/components/Select';
import { Toggle } from '../../../shared/components/Toggle';
import { PriceInput } from '../../../shared/components/PriceInput';
import Button from '../../../shared/components/Button';
import { COUNTRIES } from '../../../shared/data/countries';
import { NG_STATE_OPTIONS } from '../../../shared/data/nigerianStates';
import { parsePriceToKobo, koboToInputValue } from '../../../shared/utils/priceUtils';
import { RateType, ZoneType, type ShippingZone } from '../api/shippingZonesApi';
import { WeightTierRows } from './WeightTierRows';
import { validateZoneForm, type ZoneFormValues } from '../payload';

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ title: c.name, value: c.code }));

const TYPE_OPTIONS: { value: ZoneType; title: string }[] = [
  { value: ZoneType.REGION, title: 'Region' },
  { value: ZoneType.LOCAL_PICKUP, title: 'Local pickup' },
];

const RATE_TYPE_OPTIONS: { value: RateType; title: string }[] = [
  { value: RateType.FREE, title: 'Free' },
  { value: RateType.FLAT, title: 'Flat' },
  { value: RateType.WEIGHT_BASED, title: 'Weight-based' },
];

/** Surfaced when the backend rejects a create/update with 409 Conflict. */
export interface ConflictInfo {
  status: number;
  message?: string;
}

interface Props {
  initial?: ShippingZone | null;
  values: ZoneFormValues;
  onChange: (values: ZoneFormValues) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSaving: boolean;
  conflict?: ConflictInfo | null;
}

/**
 * Create/edit a shipping zone (PE-2172/PE-2173). `type` is immutable once created —
 * the backend ignores it on update, so the picker is locked in edit mode to avoid a
 * silently-ignored change.
 */
export function ShippingZoneForm({ initial, values, onChange, onSubmit, onCancel, isSaving, conflict }: Props) {
  const [errors, setErrors] = useState<string[]>([]);
  const isEditing = !!initial;
  const isPickup = values.type === ZoneType.LOCAL_PICKUP;
  const isNigeria = values.countryCode === 'NG';

  const set = <K extends keyof ZoneFormValues>(key: K, value: ZoneFormValues[K]) =>
    onChange({ ...values, [key]: value });

  const toggleState = (code: string) =>
    set('stateCodes', values.stateCodes.includes(code) ? values.stateCodes.filter((c) => c !== code) : [...values.stateCodes, code]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateZoneForm(values);
    setErrors(validationErrors);
    if (validationErrors.length > 0) return;
    onSubmit();
  };

  const conflictMessage = (() => {
    if (!conflict || conflict.status !== 409) return null;
    const raw = (conflict.message || '').toLowerCase();
    if (raw.includes('pickup') || raw.includes('one local')) {
      return 'Only one local-pickup zone is allowed.';
    }
    return 'A zone with this name already exists.';
  })();

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Zone name"
        placeholder="e.g. Lagos Mainland"
        value={values.name}
        onChange={(e) => set('name', e.target.value)}
      />

      <div className="max-w-[280px]">
        <Select
          label="Type"
          options={TYPE_OPTIONS.map((o) => ({ title: o.title, value: o.value }))}
          value={values.type}
          onChange={(v) => set('type', v as ZoneType)}
          disabled={isEditing}
        />
        {isEditing && <p className="text-[12px] text-muted-foreground mt-1">Type cannot be changed after a zone is created.</p>}
      </div>

      {isPickup ? (
        <p className="text-[13px] text-foreground bg-muted rounded-lg px-3 py-2">
          Free at checkout; the customer collects in store.
        </p>
      ) : (
        <>
          <div className="max-w-[320px]">
            <Select
              label="Country"
              options={COUNTRY_OPTIONS}
              value={values.countryCode}
              onChange={(v) => set('countryCode', v)}
            />
          </div>

          {isNigeria && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[14px] font-[600] text-foreground">States covered</label>
                <Toggle
                  label="All states in country"
                  checked={values.allStates}
                  onChange={(checked) => onChange({ ...values, allStates: checked, stateCodes: checked ? [] : values.stateCodes })}
                />
              </div>
              {!values.allStates && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[220px] overflow-y-auto border border-[#D9D9D9] rounded-[10px] p-3">
                  {NG_STATE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 text-[13px] text-foreground">
                      <input
                        type="checkbox"
                        checked={values.stateCodes.includes(opt.value)}
                        onChange={() => toggleState(opt.value)}
                      />
                      {opt.title}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-[14px] font-[600] text-foreground mb-1">Rate</label>
            <div className="flex gap-4 mb-3">
              {RATE_TYPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-[14px] text-foreground">
                  <input
                    type="radio"
                    name="rateType"
                    checked={values.rateType === opt.value}
                    onChange={() => set('rateType', opt.value)}
                  />
                  {opt.title}
                </label>
              ))}
            </div>
            {values.rateType === RateType.FLAT && (
              <div className="max-w-[240px]">
                <PriceInput
                  label="Flat rate"
                  value={parsePriceToKobo(values.flatAmountNaira)}
                  onChange={(kobo) => set('flatAmountNaira', koboToInputValue(kobo))}
                />
              </div>
            )}
            {values.rateType === RateType.WEIGHT_BASED && (
              <WeightTierRows tiers={values.weightTiers} onChange={(t) => set('weightTiers', t)} />
            )}
          </div>
        </>
      )}

      <div className="grid grid-cols-3 gap-4">
        <Input
          label="Priority (lower wins)"
          type="number"
          value={values.priority}
          onChange={(e) => set('priority', e.target.value)}
        />
        <Input
          label="Min delivery (days)"
          type="number"
          min={0}
          value={values.etaMinDays}
          onChange={(e) => set('etaMinDays', e.target.value)}
        />
        <Input
          label="Max delivery (days)"
          type="number"
          min={0}
          value={values.etaMaxDays}
          onChange={(e) => set('etaMaxDays', e.target.value)}
        />
      </div>

      <Toggle label="Active" checked={values.isActive} onChange={(checked) => set('isActive', checked)} />

      {errors.length > 0 && (
        <div className="text-[13px] text-red-500 space-y-1">
          {errors.map((err) => (
            <p key={err}>{err}</p>
          ))}
        </div>
      )}
      {conflictMessage && <p className="text-[13px] text-red-500">{conflictMessage}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" size="sm" className="h-[40px]" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" className="h-[40px]" disabled={isSaving}>
          {isSaving ? 'Saving…' : isEditing ? 'Save changes' : 'Create zone'}
        </Button>
      </div>
    </form>
  );
}
